import type { CSSProperties, ReactNode } from "react";
import { CheckCircle2, Circle, Loader2, XCircle } from "lucide-react";

export type RequestProgressStepState = "complete" | "current" | "pending" | "error";

export interface RequestProgressStep {
  id: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  tooltip?: string;
  state: RequestProgressStepState;
}

interface RequestProgressStepperProps {
  steps: RequestProgressStep[];
  className?: string;
  size?: "default" | "compact";
}

type StepperStyle = CSSProperties & { "--stepper-columns": string };

const getStepTone = (state: RequestProgressStepState) => {
  switch (state) {
    case "complete":
      return {
        marker: "border-green-500 bg-green-500 text-white dark:border-green-300 dark:bg-green-300 dark:text-[#04133D]",
        line: "bg-green-500/70 dark:bg-green-300/70",
        label: "text-green-700 dark:text-green-100",
        description: "text-green-600/90 dark:text-green-200/70",
      };
    case "current":
      return {
        marker: "border-border bg-muted text-muted-foreground",
        line: "bg-foreground/10",
        label: "text-foreground/75",
        description: "text-foreground/45",
      };
    case "error":
      return {
        marker: "border-red-500 bg-red-500/15 text-red-600 dark:border-red-300 dark:bg-red-300/15 dark:text-red-300",
        line: "bg-foreground/10",
        label: "text-red-700 dark:text-red-100",
        description: "text-red-600/90 dark:text-red-200/70",
      };
    case "pending":
    default:
      return {
        marker: "border-border bg-muted/50 text-foreground/45",
        line: "bg-foreground/10",
        label: "text-foreground/55",
        description: "text-foreground/35",
      };
  }
};

const StepIcon = ({ state, compact }: { state: RequestProgressStepState; compact: boolean }) => {
  const iconClass = compact ? "h-3 w-3" : "h-4 w-4";
  const pendingClass = compact ? "h-2.5 w-2.5" : "h-3 w-3";
  if (state === "complete") return <CheckCircle2 className={iconClass} />;
  if (state === "current") return <Loader2 className={`${iconClass} animate-spin`} />;
  if (state === "error") return <XCircle className={iconClass} />;
  return <Circle className={pendingClass} />;
};

const RequestProgressStepper = ({
  steps,
  className = "",
  size = "default",
}: RequestProgressStepperProps) => {
  const compact = size === "compact";
  const markerSize = compact ? "h-6 w-6" : "h-8 w-8";
  const lineTop = compact ? "top-3" : "top-4";
  const lineLeft = compact ? "left-6" : "left-8";

  return (
    <div
      className={`grid gap-2 sm:gap-3 sm:[grid-template-columns:var(--stepper-columns)] ${className}`}
      style={{ "--stepper-columns": `repeat(${steps.length}, minmax(0, 1fr))` } as StepperStyle}
    >
      {steps.map((step, index) => {
        const tone = getStepTone(step.state);
        const isLast = index === steps.length - 1;

        return (
          <div key={step.id} className="group relative min-w-0">
            {!isLast && (
              <div
                className={`absolute ${lineLeft} right-[-0.5rem] ${lineTop} hidden h-px sm:block ${tone.line}`}
                aria-hidden="true"
              />
            )}
            <div className="relative flex gap-2 sm:flex-col sm:gap-1.5">
              <div
                className={`flex ${markerSize} shrink-0 items-center justify-center rounded-full border ${tone.marker}`}
                aria-label={step.tooltip ?? step.label}
              >
                {step.icon ? (
                  <span className={compact ? "scale-75" : undefined}>{step.icon}</span>
                ) : (
                  <StepIcon state={step.state} compact={compact} />
                )}
              </div>
              <div className="min-w-0">
                <div className={`${compact ? "text-[10px]" : "text-xs"} font-semibold leading-4 ${tone.label}`}>
                  {step.label}
                </div>
                {step.description && (
                  <div className={`mt-0.5 ${compact ? "text-[10px]" : "text-[11px]"} leading-4 ${tone.description}`}>
                    {step.description}
                  </div>
                )}
              </div>
            </div>
            {(step.tooltip || step.description) && (
              <div className="pointer-events-none absolute bottom-full left-0 z-20 mb-2 hidden w-48 rounded-lg border border-border dropdown-panel px-3 py-2 text-[11px] leading-4 text-foreground shadow-xl group-hover:block">
                {step.tooltip ?? step.description}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RequestProgressStepper;
